/**
 * Glass Intensity computation — single source of truth for the
 * 0–100% slider that drives `backdrop-filter: blur(...)` and the
 * `--lg-card-bg-*` opacity vars used by .glass-card / .lg-glass-*.
 *
 * Two-piece curve:
 *   0     → fully crystal/clear
 *   80    → defaults (the visual reference design)
 *   100   → boosted top-end to compensate for Chrome's weaker
 *           backdrop-filter rasterization vs Safari.
 *
 * Used by:
 *   - SettingsPages.applyGlassIntensity (live slider drag)
 *   - CommunityPortal mount-effect (persisted value rehydration)
 *   - App.js global mount-effect (persisted value rehydration)
 *   - AppearanceWidget.applyGlassIntensity (mirrored helper)
 */

export function applyGlassIntensity(pct) {
  const p = Math.max(0, Math.min(100, pct || 0));
  // Blur factor — modest boost above 80 (caps at ≈1.6× of defaults)
  const factor = p <= 80 ? (p / 80) : (1 + ((p - 80) / 20) * 0.6);
  // Opacity factor — steeper ramp above 80 so cards become visibly
  // frosted at high settings even in Chrome (caps at ≈2.7× of defaults)
  const oFactor = p <= 80 ? (p / 80) : (1 + ((p - 80) / 20) * 1.7);

  const b1 = Math.max(2, Math.round(14 * factor));
  const b2 = Math.max(4, Math.round(22 * factor));
  const b3 = Math.max(4, Math.round(24 * factor));
  const bShell = Math.max(4, Math.round(28 * factor));
  // White "sheen" tint layered on top of the solid surface — kept subtle; the
  // solid layer below now carries opacity so this only adds a glassy highlight.
  const aDark = (0.04 * oFactor).toFixed(3);
  const aLight = (0.14 * oFactor).toFixed(3);
  // Opacity of the solid card surface. 0% → 0.12 (near-crystal), 100% → 1.0
  // (fully opaque, no transparency — maximum text contrast). Never fully
  // invisible so cards always have a readable floor.
  const solid = (0.12 + 0.88 * (p / 100)).toFixed(3);

  const root = document.documentElement;
  root.style.setProperty('--lg-blur-1', `${b1}px`);
  root.style.setProperty('--lg-blur-2', `${b2}px`);
  root.style.setProperty('--lg-blur-3', `${b3}px`);
  root.style.setProperty('--lg-blur-shell', `${bShell}px`);
  root.style.setProperty('--lg-card-bg-dark', aDark);
  root.style.setProperty('--lg-card-bg-light', aLight);
  root.style.setProperty('--lg-solid', solid);

  // Chrome desktop caches backdrop-filter raster layers during slider drags.
  // Briefly toggle a class that forces a composite-layer refresh on every
  // glass element so the new blur value is rasterized instantly.
  root.classList.add('lg-repaint');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => root.classList.remove('lg-repaint'));
  });
}
